import { Menu, MenuButton, MenuItem, MenuList, Text, VStack } from "@chakra-ui/react";
import React from "react";
import EntityModalButton from "../../../components/EntityModalButton";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../components/EntityProfileDataTable";
import { entityProfileModalColors } from "../../../entityProfileModal.theme";
import type {
  PatientCaregiverRelationship,
  RelatedEntity,
  RelationshipWithDisplayData,
} from "../caregiverRelationships.types";
import {
  formatDate,
  formatRelatedEntityLabel,
  getRelatedEntityId,
  getRelatedEntityName,
} from "../caregiverRelationships.utils";

type Props = {
  isDeleting: boolean;
  relatedEntity: RelatedEntity;
  rows: RelationshipWithDisplayData[];
  onDelete: (relationship: PatientCaregiverRelationship) => void;
  onEdit: (relationship: PatientCaregiverRelationship) => void;
  onOpenRelatedEntity?: (relationship: RelationshipWithDisplayData) => void;
};

type RelationshipSortKey = "relatedEntity" | "startDate" | "endDate" | "relationship";

export default function RelationshipTable(props: Props) {
  const { isDeleting, onDelete, onEdit, onOpenRelatedEntity, relatedEntity } = props;
  const columns = React.useMemo<
    Array<EntityProfileDataTableColumn<RelationshipWithDisplayData, RelationshipSortKey>>
  >(
    () => [
      {
        key: "relatedEntity",
        header: relatedEntity === "caregiver" ? "Caregiver" : "Patient",
        render: (row) => (
          <VStack alignItems="flex-start" spacing={0}>
            <EntityModalButton
              kind="blue"
              style={{
                color: entityProfileModalColors.active,
                fontWeight: 600,
                textAlign: "left",
                whiteSpace: "normal",
              }}
              variant="link"
              onClick={() => onOpenRelatedEntity?.(row)}
            >
              {formatRelatedEntityLabel(row, relatedEntity)}
            </EntityModalButton>
            {getRelatedEntityName(row, relatedEntity) === "" ? null : (
              <Text color={entityProfileModalColors.muted} fontSize="12px">
                ID {getRelatedEntityId(row, relatedEntity)}
              </Text>
            )}
          </VStack>
        ),
        sortKey: "relatedEntity",
        sortValue: (row) => formatRelatedEntityLabel(row, relatedEntity),
      },
      {
        key: "from",
        header: "From",
        render: (row) => formatDate(row.startDate),
        sortKey: "startDate",
        sortValue: (row) => String(row.startDate ?? ""),
      },
      {
        key: "to",
        header: "To",
        render: (row) => formatDate(row.endDate),
        sortKey: "endDate",
        sortValue: (row) => String(row.endDate ?? ""),
      },
      {
        key: "relationship",
        header: "Relationship",
        render: (row) => row.relationshipName,
        sortKey: "relationship",
        sortValue: (row) => row.relationshipName,
      },
      {
        key: "actions",
        cellProps: {
          textAlign: "center",
          whiteSpace: "nowrap",
        },
        header: "Action",
        render: (row) => (
          <Menu placement="bottom-end">
            {/* Chakra owns the trigger element, so it carries the bootstrap
                classes directly instead of rendering an EntityModalButton. */}
            <MenuButton
              aria-label="Relationship actions"
              className="btn btn-info"
              style={{
                alignItems: "center",
                borderRadius: "50%",
                display: "inline-flex",
                height: 38,
                justifyContent: "center",
                minWidth: 38,
                padding: 0,
                width: 38,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <i className="fa fa-ellipsis-v" />
            </MenuButton>
            <MenuList
              fontSize="14px"
              minW="96px"
              py={0}
              onClick={(event) => event.stopPropagation()}
            >
              <MenuItem fontWeight={700} onClick={() => onEdit(row)}>
                Edit
              </MenuItem>
              <MenuItem fontWeight={700} isDisabled={isDeleting} onClick={() => onDelete(row)}>
                Delete
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      },
    ],
    [isDeleting, onDelete, onEdit, onOpenRelatedEntity, relatedEntity]
  );

  return (
    <EntityProfileDataTable
      columns={columns}
      getRowKey={(row) => row.id}
      rows={props.rows}
      showEmptyState={false}
      tableSx={{
        tableLayout: "fixed",
        width: "100%",
        "& th, & td": {
          width: "20%",
        },
      }}
    />
  );
}
